import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PanelHeader, Field } from './_shared';

export default function BudgetPanel() {
  const [cfg, setCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [defaultCap, setDefaultCap] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [c, s] = await Promise.all([
        adminApi.config(),
        adminApi.budget(),
      ]);
      setCfg(c);
      setSummary(s);
      // iter-26 — keep the input blank when no value has been recorded yet.
      // The PUT handler treats 0 as "no cap" so we must distinguish null /
      // missing (blank input) from 0 (explicit unlimited).
      const has = Object.prototype.hasOwnProperty.call(c, 'monthly_budget_default_usd')
                  && c.monthly_budget_default_usd != null;
      setDefaultCap(has ? String(c.monthly_budget_default_usd) : '');
    } catch { toast.error('Could not load budget data'); }
  };
  useEffect(() => { reload(); }, []);

  const saveDefault = async () => {
    const v = defaultCap.trim();
    const value = v === '' ? 0 : parseFloat(v);  // backend treats 0 as "no cap"
    if (Number.isNaN(value) || value < 0) return toast.error('Enter a non-negative number.');
    setBusy(true);
    try {
      await adminApi.saveConfig({ monthly_budget_default_usd: value });
      toast.success(value === 0 ? 'Default cap removed (unlimited).' : `Default cap set to $${value.toFixed(2)}/month.`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!cfg || !summary) return <section><PanelHeader title="AI Budget" /><div className="mt-4 text-[12px] text-muted-foreground">Loading…</div></section>;

  const rows = summary.rows || [];
  const tierCaps = summary.tier_caps || [];
  const totalSpend = rows.reduce((s, r) => s + (r.est_cost_usd || 0), 0);

  return (
    <section>
      <PanelHeader
        title="AI Budget"
        subtitle="Monthly USD cap on AI generation. Project-level cap (set by customers) overrides this default. Each generation records an estimated cost based on model multiplier × tokens output."
        onRefresh={reload}
      />

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <div className="rounded-md border border-border bg-card p-4 lg:col-span-1">
          <div className="text-[12.5px] font-semibold mb-2">App-wide default cap</div>
          <Field label="Cap per project per month (USD). 0 = no cap." className="max-w-full">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={defaultCap}
              onChange={(e) => setDefaultCap(e.target.value)}
              placeholder="e.g. 25.00 (or 0 for unlimited)"
              className="h-8 font-mono-stat text-[12px]"
              data-testid="admin-budget-default-input"
            />
          </Field>
          <Button size="sm" onClick={saveDefault} disabled={busy} className="mt-3 gap-1.5" data-testid="admin-budget-default-save"><Save className="h-3.5 w-3.5" /> Save default</Button>
          <div className="mt-3 text-[10.5px] text-muted-foreground">
            Current: {cfg.monthly_budget_default_usd == null
              ? <b className="text-foreground" data-testid="admin-budget-current-null">— not yet recorded —</b>
              : <b className="text-foreground">${Number(cfg.monthly_budget_default_usd).toFixed(2)}</b>}
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="text-[12.5px] font-semibold">Spend this month — {summary.month || new Date().toISOString().slice(0,7)}</div>
            <div className="font-mono-stat text-[11.5px] text-muted-foreground">total ${totalSpend.toFixed(2)}</div>
          </div>
          <div className="mt-3 rounded-md border border-border">
            <div className="grid grid-cols-12 gap-2 border-b border-border bg-panel-header/40 px-3 py-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <div className="col-span-4">Project</div>
              <div className="col-span-3">User</div>
              <div className="col-span-2 text-right">Tokens</div>
              <div className="col-span-2 text-right">Spend</div>
              <div className="col-span-1 text-right">Cap</div>
            </div>
            {rows.length === 0 && <div className="px-3 py-6 text-[12px] text-muted-foreground">No usage this month.</div>}
            {rows.map((r) => {
              const cap = r.cap_usd;
              const pct = cap ? Math.min(100, (r.est_cost_usd / cap) * 100) : 0;
              return (
                <div key={`${r.user_id}_${r.project_id}`} className="grid grid-cols-12 items-center gap-2 border-b border-border px-3 py-2 text-[12px] last:border-b-0" data-testid={`admin-budget-row-${r.project_id}`}>
                  <div className="col-span-4 truncate" title={r.project_name}>{r.project_name}</div>
                  <div className="col-span-3 truncate font-mono-stat text-[11px] text-muted-foreground" title={r.user_id}>{r.user_id.slice(-12)}</div>
                  <div className="col-span-2 text-right font-mono-stat text-[11px]">{r.tokens.toLocaleString()}</div>
                  <div className="col-span-2 text-right">
                    <div className="font-mono-stat text-[11.5px]">
                      {r.est_cost_usd == null ? '—' : `$${r.est_cost_usd.toFixed(2)}`}
                    </div>
                    {cap != null && (
                      <div className="mt-0.5 ml-auto h-1 w-20 overflow-hidden rounded-sm bg-secondary">
                        <div className={cn('h-full', pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary')} style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono-stat text-[11px]">
                    {cap == null ? '—' : (
                      r.over_cap
                        ? <span className="inline-flex items-center gap-1 text-destructive"><AlertTriangle className="h-3 w-3" /> ${Number(cap).toFixed(0)}</span>
                        : <span>${Number(cap).toFixed(0)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 2026-10 — Tier safety-cap monitor (max_llm_spend_cents per plan). */}
      <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-tier-safety-caps">
        <div className="flex items-center justify-between">
          <div className="text-[12.5px] font-semibold">Tier safety caps — real usage this month</div>
          <div className="text-[10.5px] text-muted-foreground">
            {tierCaps.filter((r) => r.near_cap).length} near/over cap
          </div>
        </div>
        <p className="mt-1 text-[10.5px] text-muted-foreground">
          Per-user dollar ceiling (`max_llm_spend_cents`) set on each plan tier. Shows every user with any spend this month against their tier's cap.
        </p>
        <div className="mt-3 rounded-md border border-border">
          <div className="grid grid-cols-12 gap-2 border-b border-border bg-panel-header/40 px-3 py-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Tier</div>
            <div className="col-span-3 text-right">Spend / Cap</div>
            <div className="col-span-3 text-right">Status</div>
          </div>
          {tierCaps.length === 0 && (
            <div className="px-3 py-6 text-[12px] text-muted-foreground">No tier-capped users have spent anything this month.</div>
          )}
          {tierCaps.map((r) => (
            <div key={r.user_id} className="grid grid-cols-12 items-center gap-2 border-b border-border px-3 py-2 text-[12px] last:border-b-0" data-testid={`admin-tier-cap-row-${r.user_id}`}>
              <div className="col-span-4 truncate" title={r.user_id}>{r.email || r.user_id.slice(-12)}</div>
              <div className="col-span-2 truncate text-[11px] text-muted-foreground capitalize">{r.tier}</div>
              <div className="col-span-3 text-right">
                <div className="font-mono-stat text-[11.5px]">
                  ${(r.spent_cents / 100).toFixed(2)} / ${(r.limit_cents / 100).toFixed(2)}
                </div>
                <div className="mt-0.5 ml-auto h-1 w-20 overflow-hidden rounded-sm bg-secondary">
                  <div className={cn('h-full', r.over_cap ? 'bg-destructive' : r.near_cap ? 'bg-warning' : 'bg-primary')} style={{ width: `${Math.min(100, r.pct)}%` }} />
                </div>
              </div>
              <div className="col-span-3 text-right">
                {r.over_cap ? (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-destructive" data-testid={`admin-tier-cap-over-${r.user_id}`}>
                    <AlertTriangle className="h-3 w-3" /> Over cap ({r.pct.toFixed(0)}%)
                  </span>
                ) : r.near_cap ? (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-warning" data-testid={`admin-tier-cap-near-${r.user_id}`}>
                    <AlertTriangle className="h-3 w-3" /> Near cap ({r.pct.toFixed(0)}%)
                  </span>
                ) : (
                  <span className="text-[10.5px] text-muted-foreground">{r.pct.toFixed(0)}%</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
